import React, { useState } from 'react';
import {
  Star,
  Pencil,
  Trash2,
  Plus,
  X,
  CreditCard,
  AlertTriangle,
  Loader2,
} from 'lucide-react';
import { BillingAccount, PaymentMethod, getStatusBadge } from './billingTypes';
import { CreditBreakdown } from '../../services/billingApi';

// Helper to check if card is expiring soon
const getExpiryWarning = (pm: PaymentMethod): { warn: boolean; days: number | null; expired: boolean } => {
  if (!pm.exp_month || !pm.exp_year) return { warn: false, days: null, expired: false };
  
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  
  // Card expires at end of exp_month
  const expYear = pm.exp_year < 100 ? 2000 + pm.exp_year : pm.exp_year;
  const expiryDate = new Date(expYear, pm.exp_month, 0); // Last day of exp_month
  
  const diffTime = expiryDate.getTime() - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  if (diffDays < 0) return { warn: true, days: 0, expired: true };
  if (diffDays <= 30) return { warn: true, days: diffDays, expired: false };
  return { warn: false, days: diffDays, expired: false };
};


interface BillingAccountCardProps {
  account: BillingAccount;
  onEdit: (account: BillingAccount) => void;
  onDelete: (id: number) => void;
  onSetDefault: (id: number) => void;
  onAddPaymentMethod: (accountId: number) => void;
  onSetDefaultPaymentMethod: (id: number) => void;
onDeletePaymentMethod: (id: number) => void;
  onGetCredits: (accountId: number) => Promise<CreditBreakdown>;
  loading?: boolean;
  isOnlyAccount?: boolean;
  softLaunchMode?: boolean;
}

const BillingAccountCard: React.FC<BillingAccountCardProps> = ({
  account,
  onEdit,
  onDelete,
  onSetDefault,
  onAddPaymentMethod,
  onSetDefaultPaymentMethod,
onDeletePaymentMethod,
  onGetCredits,
  loading = false,
  isOnlyAccount = false,
  softLaunchMode = false,
}) => {
const [hoveredAction, setHoveredAction] = useState<string | null>(null);
const [hoveredPmAction, setHoveredPmAction] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [creditInfo, setCreditInfo] = useState<CreditBreakdown | null>(null);
  const [creditsLoading, setCreditsLoading] = useState(false);
  const [creditsError, setCreditsError] = useState(false);

// Delete validation
  const hasWorkspaces = (account.workspaces?.length || 0) > 0;
  const isDefault = account.is_default;

  // An account with no workspace can be deleted even if it still has payment methods —
  // the backend removes the cards first. Workspaces must be removed before deleting.
  const canDelete = !isDefault && !hasWorkspaces && !isOnlyAccount;

const getDeleteTooltip = (): string | null => {
    if (isOnlyAccount) return 'Cannot delete your only billing account';
    if (isDefault) return 'Cannot delete the default account';
    if (hasWorkspaces) return 'Remove associated workspaces first';
    return null;
  };

  const openDeleteConfirm = async () => {
    if (!canDelete) return;
    setShowDeleteConfirm(true);
    setCreditInfo(null);
    setCreditsError(false);
    setCreditsLoading(true);
    try {
      const c = await onGetCredits(account.id);
      setCreditInfo(c);
    } catch {
      setCreditsError(true);
    } finally {
      setCreditsLoading(false);
    }
  };

  const fmtCents = (cents: number) => `$${(cents / 100).toFixed(2)}`;
  const refundableBalance = (creditInfo?.refundable_credits || 0) + (creditInfo?.excess_payments || 0);
  const promoBalance = creditInfo?.promotional_credits || 0;
  const blockedByRefundable = !creditsLoading && !creditsError && refundableBalance > 0;

  // Styles
  const cardStyle: React.CSSProperties = {
    backgroundColor: 'var(--v2-surface)',
    borderRadius: 'var(--radius-lg)',
    border: '1px solid var(--v2-border)',
    boxShadow: 'var(--shadow-sm)',
    overflow: 'hidden',
  };

  const headerStyle: React.CSSProperties = {
    padding: '20px 24px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    borderBottom: '1px solid var(--v2-border)',
    backgroundColor: account.is_default ? 'var(--v2-surface-alt)' : 'var(--v2-surface)',
  };

  const titleStyle: React.CSSProperties = {
    fontSize: '1.0625rem',
    fontWeight: 600,
    color: 'var(--v2-text)',
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  };

  const badgeStyle: React.CSSProperties = {
    display: 'inline-block',
    padding: '3px 8px',
    backgroundColor: 'var(--v2-primary-light)',
    color: 'var(--v2-primary)',
    borderRadius: 'var(--radius-full)',
    fontSize: '0.6875rem',
    fontWeight: 600,
    textTransform: 'uppercase',
  };

  const subtextStyle: React.CSSProperties = {
    fontSize: '0.875rem',
    color: 'var(--v2-text-tertiary)',
    margin: '4px 0 0 0',
  };

  const actionButtonStyle = (hovered: boolean, variant: 'default' | 'danger' = 'default'): React.CSSProperties => ({
    background: 'none',
    border: 'none',
    padding: '6px 10px',
    borderRadius: 'var(--radius-sm)',
    cursor: loading ? 'not-allowed' : 'pointer',
    fontSize: '0.8125rem',
    fontWeight: 500,
    color: variant === 'danger' ? 'var(--v2-error)' : 'var(--v2-text-tertiary)',
    backgroundColor: hovered ? (variant === 'danger' ? 'var(--v2-error-bg)' : 'var(--v2-surface-alt)') : 'transparent',
    transition: 'all 0.15s',
    opacity: loading ? 0.5 : 1,
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
  });

  const sectionStyle: React.CSSProperties = {
    padding: '20px 24px',
  };

  const sectionTitleStyle: React.CSSProperties = {
    fontSize: '0.6875rem',
    fontWeight: 600,
    color: 'var(--v2-text-muted)',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    margin: '0 0 12px 0',
  };

  const paymentMethodRowStyle = (hovered: boolean): React.CSSProperties => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 14px',
    backgroundColor: hovered ? 'var(--v2-surface-alt)' : 'var(--v2-bg)',
    borderRadius: 'var(--radius-md)',
    marginBottom: '8px',
    transition: 'background-color 0.15s',
  });

  const pmIconStyle: React.CSSProperties = {
    width: '40px',
    height: '28px',
    backgroundColor: 'var(--v2-surface)',
    border: '1px solid var(--v2-border)',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.6875rem',
    fontWeight: 600,
    color: 'var(--v2-text-tertiary)',
  };

  const alertStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    padding: '12px 16px',
    backgroundColor: 'var(--v2-warning-bg)',
    border: '1px solid #fcd34d',
    borderRadius: 'var(--radius-md)',
    color: '#92400e',
    fontSize: '0.875rem',
  };

  const workspaceChipStyle: React.CSSProperties = {
    display: 'inline-block',
    padding: '4px 10px',
    backgroundColor: 'var(--v2-bg)',
    color: 'var(--v2-text-secondary)',
    borderRadius: 'var(--radius-sm)',
    fontSize: '0.8125rem',
    fontWeight: 500,
    marginRight: '8px',
    marginBottom: '8px',
  };

  const addButtonStyle = (hovered: boolean): React.CSSProperties => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 14px',
    backgroundColor: hovered ? 'var(--v2-primary-hover)' : 'var(--v2-primary)',
    color: '#ffffff',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    fontSize: '0.8125rem',
    fontWeight: 500,
    cursor: loading ? 'not-allowed' : 'pointer',
    transition: 'background-color 0.15s',
    opacity: loading ? 0.5 : 1,
  });

  const getBrandDisplay = (pm: PaymentMethod) => {
    const brandMap: Record<string, string> = {
      visa: 'VISA',
      mastercard: 'MC',
      amex: 'AMEX',
      discover: 'DISC',
    };
    return brandMap[pm.brand?.toLowerCase() || ''] || pm.brand?.toUpperCase() || pm.type.toUpperCase();
  };

  return (
    <div style={cardStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <div>
          <h3 style={titleStyle}>
            {account.name}
            {account.is_default && <span style={badgeStyle}>Default</span>}
          </h3>
          {account.company_name && <p style={subtextStyle}>{account.company_name}</p>}
          {account.billing_email && <p style={subtextStyle}>{account.billing_email}</p>}
        </div>
        <div style={{ display: 'flex', gap: '4px' }}>
          {!account.is_default && (
            <button
              onClick={() => onSetDefault(account.id)}
              disabled={loading}
              onMouseOver={() => setHoveredAction('default')}
              onMouseOut={() => setHoveredAction(null)}
              style={actionButtonStyle(hoveredAction === 'default')}
              title="Set as default"
            >
              <Star size={14} strokeWidth={1.5} />
              Set Default
            </button>
          )}
          <button
            onClick={() => onEdit(account)}
            disabled={loading}
            onMouseOver={() => setHoveredAction('edit')}
            onMouseOut={() => setHoveredAction(null)}
            style={actionButtonStyle(hoveredAction === 'edit')}
          >
            <Pencil size={14} strokeWidth={1.5} />
            Edit
          </button>
<button
onClick={() => openDeleteConfirm()}
            disabled={loading || !canDelete}
            onMouseOver={() => setHoveredAction('delete')}
            onMouseOut={() => setHoveredAction(null)}
            style={{
              ...actionButtonStyle(hoveredAction === 'delete' && canDelete, 'danger'),
              opacity: canDelete ? 1 : 0.4,
              cursor: canDelete ? 'pointer' : 'not-allowed',
            }}
            title={getDeleteTooltip() || 'Delete billing account'}
          >
            <Trash2 size={14} strokeWidth={1.5} />
            Delete
          </button>
        </div>
      </div>

      {/* Payment Methods */}
      <div style={sectionStyle}>
        <h4 style={sectionTitleStyle}>Payment Methods</h4>

        {(account.paymentMethods?.length || 0) === 0 ? (
          <div style={alertStyle}>
            <AlertTriangle size={16} strokeWidth={1.5} style={{ flexShrink: 0, marginTop: '1px' }} />
            <span>No payment method added. Add one to enable automatic billing.</span>
          </div>
        ) : (
          <div>
            {account.paymentMethods.map((pm) => {
              const statusBadge = getStatusBadge(pm.status);
              const pmKey = `pm-${pm.id}`;

              return (
                <div
                  key={pm.id}
                  style={paymentMethodRowStyle(hoveredPmAction?.startsWith(pmKey) || false)}
                  onMouseOver={() => setHoveredPmAction(pmKey)}
                  onMouseOut={() => setHoveredPmAction(null)}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={pmIconStyle}>{getBrandDisplay(pm)}</div>
                    <div>
                      <div style={{ fontSize: '0.9rem', fontWeight: 500, color: 'var(--v2-text)' }}>
                        •••• {pm.last4}
                        {pm.is_default && (
                          <span
                            style={{
                              marginLeft: '8px',
                              padding: '2px 6px',
                              backgroundColor: 'var(--v2-primary-light)',
                              color: 'var(--v2-primary)',
                              borderRadius: '4px',
                              fontSize: '0.6875rem',
                              fontWeight: 600,
                            }}
                          >
                            Default
                          </span>
                        )}
                        {pm.status !== 'valid' && (
                          <span
                            style={{
                              marginLeft: '8px',
                              padding: '2px 6px',
                              backgroundColor: statusBadge.bg,
                              color: statusBadge.text,
                              borderRadius: '4px',
                              fontSize: '0.6875rem',
                              fontWeight: 600,
                            }}
                          >
                            {pm.status}
                          </span>
                        )}
                      </div>
{pm.exp_month && pm.exp_year && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontSize: '0.8125rem', color: 'var(--v2-text-tertiary)' }}>
                            Expires {pm.exp_month}/{pm.exp_year}
                          </span>
                          {(() => {
                            const expiry = getExpiryWarning(pm);
                            if (!expiry.warn) return null;
                            return (
                              <span
                                style={{
                                  padding: '2px 6px',
                                  backgroundColor: expiry.expired ? 'var(--v2-error-bg)' : 'var(--v2-warning-bg)',
                                  color: expiry.expired ? 'var(--v2-error)' : '#92400e',
                                  borderRadius: '4px',
                                  fontSize: '0.6875rem',
                                  fontWeight: 600,
                                }}
                              >
                                {expiry.expired ? 'Expired' : `Expires in ${expiry.days}d`}
                              </span>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    {!pm.is_default && (
                      <button
                        onClick={() => onSetDefaultPaymentMethod(pm.id)}
                        disabled={loading}
                        onMouseOver={() => setHoveredPmAction(`${pmKey}-default`)}
                        onMouseOut={() => setHoveredPmAction(pmKey)}
                        style={actionButtonStyle(hoveredPmAction === `${pmKey}-default`)}
                        title="Set as default"
                      >
                        <Star size={14} strokeWidth={1.5} />
                      </button>
                    )}
<button
                      onClick={() => {
                        if (account.paymentMethods.length === 1 && hasWorkspaces) return;
                        onDeletePaymentMethod(pm.id);
                      }}
                      disabled={loading || (account.paymentMethods.length === 1 && hasWorkspaces)}
                      onMouseOver={() => setHoveredPmAction(`${pmKey}-delete`)}
                      onMouseOut={() => setHoveredPmAction(pmKey)}
                      style={actionButtonStyle(hoveredPmAction === `${pmKey}-delete`, 'danger')}
                      title={account.paymentMethods.length === 1 && hasWorkspaces ? 'Remove the workspace from this account first' : 'Remove'}
                    >
                      <X size={14} strokeWidth={1.5} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

{softLaunchMode ? (
          <div style={{
            marginTop: '12px',
            padding: '10px 16px',
            backgroundColor: 'var(--v2-surface-alt)',
            border: '1px solid var(--v2-border)',
            borderRadius: 'var(--radius-md)',
            fontSize: '0.8125rem',
            color: 'var(--v2-text-muted)',
            textAlign: 'center',
          }}>
            Payment methods available after Early Access
          </div>
        ) : (
<button
  onClick={() => onAddPaymentMethod(account.id)}
  disabled={loading}
  onMouseOver={() => setHoveredAction('addPm')}
  onMouseOut={() => setHoveredAction(null)}
  style={{
    ...addButtonStyle(hoveredAction === 'addPm'),
    marginTop: '12px',
  }}
  title="Add payment method"
>
            <Plus size={14} strokeWidth={2} />
            Add Payment Method
          </button>
        )}
      </div>

 {/* Associated Workspaces */}
      {(account.workspaces?.length || 0) > 0 && (
        <div style={{ ...sectionStyle, borderTop: '1px solid var(--v2-border)', paddingTop: '16px' }}>
          <h4 style={sectionTitleStyle}>Associated Workspaces</h4>
          <div>
            {account.workspaces.map((ws) => (
              <span key={ws.id} style={workspaceChipStyle}>
                {ws.name}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Delete confirmation */}
      {showDeleteConfirm && (
        <div
          onClick={() => !loading && setShowDeleteConfirm(false)}
          style={{
            position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000, padding: '16px',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'var(--v2-surface)', borderRadius: 'var(--radius-lg)',
              border: '1px solid var(--v2-border)', boxShadow: '0 12px 32px rgba(0,0,0,0.18)',
              maxWidth: '440px', width: '100%', padding: '24px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '16px' }}>
              <div style={{
                flexShrink: 0, width: '40px', height: '40px', borderRadius: 'var(--radius-md)',
                backgroundColor: 'var(--v2-error-bg)', display: 'flex',
                alignItems: 'center', justifyContent: 'center',
              }}>
                <AlertTriangle size={20} strokeWidth={1.5} color="var(--v2-error)" />
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: '1.0625rem', fontWeight: 600, color: 'var(--v2-text)' }}>
                  Delete “{account.name}”?
                </h3>
                <p style={{ margin: '4px 0 0 0', fontSize: '0.875rem', color: 'var(--v2-text-tertiary)' }}>
                  This permanently deletes this billing account and all of its saved payment methods. This cannot be undone.
                </p>
              </div>
            </div>

{/* Credit-aware notice */}
            {creditsLoading ? (
              <div style={{
                display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px',
                fontSize: '0.875rem', color: 'var(--v2-text-tertiary)',
              }}>
                <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                Checking credit balance…
              </div>
            ) : blockedByRefundable ? (
              <div style={{ ...alertStyle, backgroundColor: 'var(--v2-error-bg)', border: '1px solid var(--v2-error)', color: 'var(--v2-error)', marginBottom: '20px' }}>
                <AlertTriangle size={16} strokeWidth={1.5} style={{ flexShrink: 0, marginTop: '1px' }} />
                <span>
                  This account holds {fmtCents(refundableBalance)} in refundable balance. Contact support
                  to refund it before this account can be deleted.
                </span>
              </div>
            ) : promoBalance > 0 ? (
              <div style={{ ...alertStyle, marginBottom: '20px' }}>
                <AlertTriangle size={16} strokeWidth={1.5} style={{ flexShrink: 0, marginTop: '1px' }} />
                <span>
                  Deleting this account will permanently forfeit {fmtCents(promoBalance)} in promotional
                  credits. They can’t be transferred to another billing account. See our{' '}
                  <a href="/legal/refunds/" target="_blank" rel="noopener noreferrer"
                    style={{ color: '#92400e', fontWeight: 600, textDecoration: 'underline' }}>
                    Refund Policy
                  </a>.
                </span>
              </div>
            ) : creditsError ? (
              <div style={{ ...alertStyle, marginBottom: '20px' }}>
                <AlertTriangle size={16} strokeWidth={1.5} style={{ flexShrink: 0, marginTop: '1px' }} />
                <span>
                  If this account holds any promotional credits, they will be permanently forfeited and
                  cannot be transferred to another billing account.
                </span>
              </div>
            ) : null}

<div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={loading}
                style={{
                  padding: '9px 16px', backgroundColor: 'transparent', color: 'var(--v2-text)',
                  border: '1px solid var(--v2-border)', borderRadius: 'var(--radius-md)',
                  fontSize: '0.875rem', fontWeight: 500, cursor: loading ? 'not-allowed' : 'pointer',
                }}
              >
                {blockedByRefundable ? 'Close' : 'Cancel'}
              </button>
              {!blockedByRefundable && (
                <button
                  onClick={() => { onDelete(account.id); setShowDeleteConfirm(false); }}
                  disabled={loading || creditsLoading}
                  style={{
                    padding: '9px 16px', backgroundColor: 'var(--v2-error)', color: '#ffffff',
                    border: 'none', borderRadius: 'var(--radius-md)', fontSize: '0.875rem',
                    fontWeight: 600, cursor: (loading || creditsLoading) ? 'not-allowed' : 'pointer',
                    opacity: (loading || creditsLoading) ? 0.6 : 1,
                  }}
                >
                  {promoBalance > 0 ? 'Forfeit credits & delete' : 'Delete account'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BillingAccountCard;

